
import { Product, Category, ProductImage } from '../types';
import { supabase, isSupabaseConfigured } from './supabaseClient';
import * as db from './db';
import { upload } from '@vercel/blob/client';

// Helper: Convert Blob to Base64 (for fallback)
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
        if (typeof reader.result === 'string') resolve(reader.result);
        else resolve('');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Helper: Convert Base64 string back to Blob (for upload)
const base64ToBlob = async (base64: string): Promise<Blob> => {
  const response = await fetch(base64);
  return await response.blob();
};

/**
 * Uploads an image or video Blob to Vercel Blob via Client SDK.
 * Returns the public URL of the uploaded asset.
 * FALLBACK: If upload fails:
 * - Images: Returns Base64 string for local persistence.
 * - Videos: Throws error (too large for local).
 */
const uploadAsset = async (asset: string | Blob): Promise<string> => {
  let blobToUpload: Blob;
  let filename: string;
  let fallbackBase64: string = '';
  let isVideo = false;

  try {
    // 1. Prepare the Blob and Fallback Base64
    if (asset instanceof Blob) {
      blobToUpload = asset;
      isVideo = asset.type.startsWith('video');
      
      // Only generate fallback for images or small videos
      if (!isVideo || asset.size < 5 * 1024 * 1024) { // Limit local fallback to 5MB
         try {
            fallbackBase64 = await blobToBase64(asset);
         } catch (e) {
            console.warn("Could not generate fallback for blob");
         }
      }

      const ext = asset.type.split('/')[1] || 'bin';
      filename = `asset-${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
    } else {
      // It's a base64 string
      fallbackBase64 = asset;
      // Basic check if it's a video base64
      isVideo = asset.startsWith('data:video');
      
      try {
        blobToUpload = await base64ToBlob(asset);
      } catch (e) {
        console.warn("Base64 to Blob conversion failed, saving as Base64.");
        return fallbackBase64;
      }
      
      const match = asset.match(/^data:(\w+)\/(\w+);base64,/);
      const ext = match ? match[2] : 'png';
      filename = `file-${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
    }

    console.log(`Starting Cloud Upload for ${filename} (${(blobToUpload.size / 1024 / 1024).toFixed(2)}MB)...`);

    // 2. Client-Side Upload (Direct to Vercel Storage)
    const newBlob = await upload(filename, blobToUpload, {
      access: 'public',
      handleUploadUrl: '/api/upload',
    });

    console.log("Cloud Upload Success:", newBlob.url);
    return newBlob.url;

  } catch (error: any) {
    console.error("Cloud Upload Failed:", error.message);
    
    if (isVideo) {
        // If it's a video and upload failed, strictly warn user because local storage will likely fail
        throw new Error("Video Cloud Upload Failed. Videos are too large to save locally. Please check your Vercel Blob configuration.");
    }

    if (fallbackBase64) {
        console.warn("Falling back to local Base64 storage for image.");
        return fallbackBase64;
    }
    throw error;
  }
};

/**
 * Helper to delete assets from Vercel Blob via API
 */
const deleteCloudAssets = async (urls: string[]) => {
  if (urls.length === 0) return;
  try {
    await fetch('/api/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ urls }),
    });
  } catch (e) {
    console.error("Failed to delete cloud assets", e);
  }
};

/**
 * Ensures all images in a product are uploaded or converted to persistable formats.
 */
const processProductAssets = async (product: Product): Promise<Product> => {
  const updatedImages: ProductImage[] = [];

  for (const img of product.images) {
    // Handle Blob URLs (e.g. from Video generation or fresh file uploads)
    if (img.url.startsWith('blob:')) {
        try {
            console.log("Processing Blob URL:", img.url);
            const response = await fetch(img.url);
            if (!response.ok) throw new Error(`Failed to fetch blob: ${response.statusText}`);
            const blob = await response.blob();
            
            const publicUrl = await uploadAsset(blob);
            updatedImages.push({ ...img, url: publicUrl });
        } catch (e: any) {
            console.error("Asset processing failed:", e);
            alert(`Failed to save asset: ${e.message}`);
            // Do not add broken asset to the product
        }
    } 
    // Handle Base64 Data URIs (e.g. from Image generation)
    else if (img.url.startsWith('data:')) {
      try {
        // If it's already a huge base64, try to upload it to clean up DB
        const publicUrl = await uploadAsset(img.url);
        updatedImages.push({ ...img, url: publicUrl });
      } catch (e) {
        console.warn("Upload failed for Base64, keeping local version.");
        // Keep original if upload fails (fallback for images)
        updatedImages.push(img);
      }
    } 
    // Handle Remote URLs
    else {
      updatedImages.push(img);
    }
  }

  return { ...product, images: updatedImages };
};

// --- Products Storage (Supabase + IndexedDB Fallback) ---

export const getProducts = async (): Promise<Product[]> => {
  // 1. Always fetch local data first (This is our fallback / offline cache)
  let localProducts: Product[] = [];
  try {
      localProducts = await db.getAll<Product>('products');
  } catch (e) {
      console.warn("IndexedDB read failed", e);
  }

  // 2. Try Supabase and Smart Merge
  if (isSupabaseConfigured) {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('createdAt', { ascending: false });

      if (!error && data) {
          const cloudProducts = data as Product[];
          const cloudMap = new Map(cloudProducts.map(p => [p.id, p]));
          const localMap = new Map(localProducts.map(p => [p.id, p]));

          const allIds = new Set([...cloudMap.keys(), ...localMap.keys()]);
          const merged: Product[] = [];

          // "Newest Wins" Strategy
          allIds.forEach(id => {
              const cloudP = cloudMap.get(id);
              const localP = localMap.get(id);

              if (cloudP && localP) {
                  // Both exist: keep the one with the newer updatedAt timestamp
                  merged.push(cloudP.updatedAt > localP.updatedAt ? cloudP : localP);
              } else if (cloudP) {
                  merged.push(cloudP);
              } else if (localP) {
                  merged.push(localP);
              }
          });

          console.log(`Synced: ${merged.length} products total.`);
          return merged.sort((a, b) => b.updatedAt - a.updatedAt);
      }
      
      console.warn("Supabase fetch failed, showing local only.", error?.message);
  }

  // 3. Fallback: Return Local only
  return localProducts.sort((a, b) => b.updatedAt - a.updatedAt);
};

export const saveProduct = async (product: Product): Promise<Product> => {
  // 1. Persist assets (Upload or Base64)
  // This step ensures we have 'clean' URLs before trying to save metadata
  const processedProduct = await processProductAssets(product);

  // Check for Massive Base64 payloads before sending to Supabase
  const hasBase64Images = processedProduct.images.some(img => img.url.startsWith('data:'));
  
  // 2. Try Supabase Sync
  if (hasBase64Images && isSupabaseConfigured) {
      console.warn("Product contains Base64 images (upload failed). Saving to Local Storage ONLY to avoid database size limits.");
  } else if (isSupabaseConfigured) {
      const { error } = await supabase
        .from('products')
        .upsert(processedProduct);
      
      if (error) {
        console.warn("Supabase save failed:", error.message);
      }
  }

  // 3. Always save to IndexedDB (Local Fallback)
  try {
      await db.put('products', processedProduct);
      return processedProduct;
  } catch (e) {
      console.error("IndexedDB save error", e);
      // QuotaExceededError is common with large videos
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
          alert("Storage Quota Exceeded. Unable to save this product locally. Please delete old items or configure Cloud Storage.");
      } else {
          alert("Failed to save product locally.");
      }
      return product;
  }
};

export const deleteProduct = async (id: string): Promise<void> => {
  // 1. Fetch the product first to identify assets
  let productToDelete: Product | undefined;

  // Try local first
  try {
     const products = await db.getAll<Product>('products');
     productToDelete = products.find(p => p.id === id);
  } catch(e) { /* ignore */ }

  // If not found locally, try cloud
  if (!productToDelete && isSupabaseConfigured) {
     const { data } = await supabase.from('products').select('*').eq('id', id).single();
     if (data) productToDelete = data as Product;
  }

  // 2. Clean up Blob Storage
  if (productToDelete) {
      const blobUrls = productToDelete.images
        .filter(img => img.url.includes('vercel-storage.com')) // Only delete vercel blob URLs
        .map(img => img.url);

      if (blobUrls.length > 0) {
          console.log(`Deleting ${blobUrls.length} assets from cloud storage...`);
          await deleteCloudAssets(blobUrls);
      }
  }

  // 3. Delete Metadata (Supabase)
  if (isSupabaseConfigured) {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);
        
      if (error) console.warn("Supabase delete failed, trying local.", error.message);
  }

  // 4. Delete Local Metadata (IndexedDB)
  try {
      await db.del('products', id);
  } catch (e) {
      console.error("IndexedDB delete error", e);
  }
};

// --- Categories Storage ---

const DEFAULT_CATEGORIES: Category[] = [
    { id: 'rings', name: 'Rings' },
    { id: 'necklaces', name: 'Necklaces' },
    { id: 'earrings', name: 'Earrings' },
    { id: 'bracelets', name: 'Bracelets' },
    { id: 'watches', name: 'Watches' },
    { id: 'custom', name: 'Custom Collection' },
];

export const getCategories = async (): Promise<Category[]> => {
    try {
        const stored = await db.getAll<Category>('categories');
        if (stored.length > 0) return stored;
    } catch (e) { /* ignore */ }

    return DEFAULT_CATEGORIES;
};

export const saveCategory = async (category: Category): Promise<void> => {
    try {
        await db.put('categories', category);
    } catch (e) {
        console.error("Failed to save category locally", e);
    }
};
